import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from '@angular/material/tree';
import {
  ArchObjHierarchyViewModel,
  ArchObjTreeViewModel,
  CostCenterClient,
  LocationClient,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import {
  SortDirection,
  SortService,
} from '@core/services/sort-service/sort.service';
import { UserService } from '@core/services/oauth-service/user.service';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserRoles } from '@shared/constants/user-roles.constants';
import {
  GANTT_GLOBAL_CONFIG,
  GanttDate,
  GanttGroup,
  GanttItem,
  GanttViewOptions,
  GanttViewType,
  NgxGanttComponent,
  NgxGanttModule,
} from '@worktile/gantt';
import { format, parse, set } from 'date-fns';
import { forkJoin } from 'rxjs';
import { EditCostcenterusageComponent } from './edit-costcenterusage/edit-costcenterusage.component';
import { AppGanttFlatComponent } from './flat/flat.component';

interface IGanttItem extends GanttItem {
  roomTitle?: string;
  workspaceTitle?: string;
  costCenterId?: string;
  areaString?: string;
}

const TREE_DATA: ArchObjTreeViewModel[] = [];

/** Flat node with expandable and level information */
interface FlatNode {
  expandable?: boolean;
  name?: string;
  level?: number;
  id?: string;
  checked?: boolean;
  type?: string;
}

@Component({
  selector: 'app-costcenter-usage',
  standalone: true,
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    OverrideModule,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    NgxGanttModule,
    MatDialogModule,
    MatTreeModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    AppGanttFlatComponent,
  ],
  providers: [
    {
      provide: GANTT_GLOBAL_CONFIG,
      useValue: {
        dateFormat: {
          hour: 'HH:mm',
          day: 'dd. MMM yyyy',
          week: 'w',
          month: 'LLL',
          quarter: 'QQQ',
          year: 'yyyy',
          yearMonth: 'LLL yyyy',
          yearQuarter: `QQQ yyyy`,
        },
      },
    },
  ],
  templateUrl: './costcenterusage.component.html',
  styleUrls: ['./costcenterusage.component.scss'],
})
export class CostcenterusageComponent implements OnInit {
  headline = this.translateService.instant(
    'menu.data_maintenance.component.costcenter_usage'
  );

  form = new FormGroup({});

  formFields: Array<FormlyFieldConfig>;

  formModel = {};

  isLoading = false;

  selectedLocation: ArchObjHierarchyViewModel;

  private transformer = (node: ArchObjTreeViewModel, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.displayName,
      level: level,
      id: node.id,
      type: node.type,
    };
  };

  treeControl = new FlatTreeControl<FlatNode>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    node => node.level,
    node => node.expandable,
    node => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  cardArchitecture: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'costcenter.costcenter_usage.architecture'
        ),
      },
    },
  };

  cardView: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'costcenter.costcenter_usage.view'
        ),
      },
    },
  };

  items: IGanttItem[] = [];

  currentYear = new Date().getFullYear();

  viewOptions: GanttViewOptions = {
    start: new GanttDate(new Date(this.currentYear, 0, 1)),
    end: new GanttDate(new Date(this.currentYear, 11, 31)),
    min: new GanttDate(new Date(this.currentYear, 0, 1)),
    max: new GanttDate(new Date(this.currentYear, +1, 11, 31)),
    cellWidth: 80,
  };

  options = {
    viewType: GanttViewType.quarter,
    draggable: false,
    styles: {
      lineHeight: 50,
      barHeight: 20,
    },
  };

  views = [
    {
      name: this.translateService.instant('costcenter.costcenter_usage.week'),
      value: GanttViewType.week,
    },
    {
      name: this.translateService.instant('costcenter.costcenter_usage.month'),
      value: GanttViewType.month,
    },
    {
      name: this.translateService.instant(
        'costcenter.costcenter_usage.quarter'
      ),
      value: GanttViewType.quarter,
    },
  ];

  selectedViewType: GanttViewType = GanttViewType.month;

  isCustomStyleActive = false;

  buttonMaxWidthTitle = this.translateService.instant(
    'costcenter.costcenter_usage.max_width'
  );

  @ViewChild('gantt') ganttComponent: NgxGanttComponent;

  selectedItems: string[] = [];

  groups: GanttGroup[] = [];

  initialArchObjId: string;

  initialArchObj: ArchObjHierarchyViewModel;

  frozenZone: { startDate: Date; endDate: Date };

  frozenStatus: boolean = false;

  description = '';

  constructor(
    private breadcrumbService: BreadcrumbService,
    private translateService: TranslateService,
    private fieldService: LocationFormlyFieldService,
    private locationClient: LocationClient,
    private dialog: MatDialog,
    private costcenterClient: CostCenterClient,
    private cd: ChangeDetectorRef,
    private readonly userService: UserService
  ) {
    this.breadcrumbService.setBreadcrumbs([
      {
        label: this.headline,
      },
    ]);
  }

  ngOnInit(): void {
    forkJoin({
      frozenZone: this.locationClient.checkFrozenZone(),
      hasGlobalRole: this.userService.checkIfUserHasRole(
        UserRoles.SMARTPORTAL_REM_DATENPFLEGE
      ),
    }).subscribe({
      next: ({ frozenZone, hasGlobalRole }) => {
        if (frozenZone && !hasGlobalRole) {
          this.frozenZone = {
            startDate: parse(frozenZone.startDate, 'dd.MM.yyyy', new Date()),
            endDate: set(parse(frozenZone.endDate, 'dd.MM.yyyy', new Date()), {
              hours: 12,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            }),
          };

          this.frozenStatus = true;
          this.description = this.translateService.instant(
            'data_maintenance.frozenZone',
            {
              frozenZoneStartDate: format(
                this.frozenZone.startDate,
                'dd.MM.yyyy'
              ),
              frozenZoneEndDate: format(this.frozenZone.endDate, 'dd.MM.yyyy'),
            }
          );
        }
      },
      error: error => {
        console.error('Error loading data:', error);
      },
    });

    this.initialArchObjId = this.getQueryParam('archObjId');
    if (this.initialArchObjId) {
      this.locationClient.getArchobjById(this.initialArchObjId).subscribe({
        next: archObj => {
          this.initialArchObj = archObj;
          this.formFields = [
            ...this.fieldService.autocompleteSearch(
              false,
              this.initialArchObj,
              true,
              id => this.onSelect(id),
              null,
              () => this.onDelete(),
              this.translateService.instant(
                'location.shared.delete_location_search_tooltip'
              )
            ),
          ];
          this.getGanttData([]);
        },
      });
    } else {
      this.formFields = [
        ...this.fieldService.autocompleteSearch(
          false,
          null,
          true,
          id => this.onSelect(id),
          null,
          () => this.onDelete(),
          this.translateService.instant(
            'location.shared.delete_location_search_tooltip'
          )
        ),
      ];
      this.getGanttData([]);
    }
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  public onSelect(archObjId: string): void {
    this.selectedItems = [];
    this.isLoading = true;

    this.locationClient.getArchobjById(archObjId).subscribe({
      next: (response: ArchObjHierarchyViewModel) => {
        this.selectedLocation = response;
        this.getTreeData(this.selectedLocation.id);
      },
      error: () => (this.isLoading = false),
    });
  }

  onDelete(): () => void {
    this.selectedLocation = null;
    this.selectedItems = [];
    this.dataSource.data = [];

    this.getGanttData([]);
    return null;
  }

  unselectAll() {
    this.isLoading = true;
    this.selectedItems = [];
    this.treeControl.dataNodes.forEach(node => (node.checked = false));
    this.getGanttData(this.selectedItems);
  }

  getTreeData(id: string) {
    this.isLoading = true;
    this.locationClient.getArchObjTree(id, null).subscribe({
      next: (res: ArchObjTreeViewModel) => {
        SortService.sortTreeChildrenByType(res, 'ZRM');
        TREE_DATA.length = 0;
        TREE_DATA.push(res);
        this.dataSource.data = TREE_DATA;
        this.isLoading = false;
      },
      error: err => {
        console.log(err);
        this.isLoading = false;
      },
    });
  }

  findObjectByName(
    data: ArchObjTreeViewModel[],
    targetId: string,
    isChild?: boolean
  ) {
    let obj;
    if (!isChild) {
      obj = data[0];
    } else {
      obj = data;
    }
    if (obj.id === targetId) {
      return obj;
    }

    if (obj.children && obj.children.length > 0) {
      for (const child of obj.children) {
        const result = this.findObjectByName(child, targetId, true);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  async getGanttData(items: string[]) {
    //Split items, because sometimes the spaceId needs to be removed
    const formattedItems = items.map(item =>
      item.split('/').slice(0, 3).join('/')
    );

    await this.costcenterClient
      .getCostCenterUsageTable(formattedItems)
      .subscribe({
        next: res => {
          this.groups = SortService.sort(
            [...(res.groups as GanttGroup[])],
            SortDirection.Ascending,
            'title'
          );
          this.items = res.items as IGanttItem[];
          this.options.viewType = GanttViewType.month;
          this.isLoading = false;
        },
      });
  }

  barClick(e) {
    if (e.item.isDummy) {
      return;
    }
    let editDialogRef = null;
    this.costcenterClient.getCostCenterUsageDetails(e.item.id).subscribe({
      next: res => {
        editDialogRef = this.dialog.open(EditCostcenterusageComponent, {
          minWidth: '50vw',
          minHeight: '60vh',
          panelClass: 'smartportal-dialog',
          data: {
            title: this.translateService.instant(
              'costcenter.costcenter_usage.edit_dialog.headline'
            ),
            room: res.title,
            costcenter: res.costCenterId ?? null,
            start: res.validFromDate,
            end: res.validUntilDate,
            freeAreaInSquareMeters: res.freeArea,
            reservedAreaInSquareMeters: res.reservedArea,
            note: res.remarks ?? null,
            recentChange: res.lastChangeInfo ?? null,
            changeReason: res.changeReasonId,
            isRoom: res.isRoom,
            isDispo: res.isDispo,
            id: e.item.id,
            archObjId: res.archObjId,
            frozenStatus: this.frozenStatus,
            description: this.description,
          },
        });
        editDialogRef?.afterClosed().subscribe({
          next: dialogResult => {
            if (dialogResult) {
              this.isLoading = true;
              this.getGanttData(this.selectedItems);
            }
          },
        });
      },
    });
  }

  getTooltip(item) {
    return `
      ${this.translateService.instant('costcenter.costcenter_usage.room')}
      ${item.group_id}
      ${
        item.workspaceTitle
          ? `
            ${this.translateService.instant('costcenter.costcenter_usage.working_space')}
            ${item.workspaceTitle}\n`
          : ''
      }
      ${this.translateService.instant('costcenter.autocomplete.costCenter')}
      ${item.costCenterId ?? '--'}\n
      ${this.translateService.instant('costcenter.costcenter_usage.share')}
      ${item.areaString ?? '--'} \n
      ${this.translateService.instant('costcenter.costcenter_usage.expiration')}
      ${this.formatUnixTimestamp(item.popupStart)} - ${this.formatUnixTimestamp(item.popupEnd)} \n
      ${this.translateService.instant('costcenter.costcenter_usage.option')}
      ${item.option ?? '--'}`;
  }

  formatUnixTimestamp(unixTimestamp: number): string {
    const date = new Date(unixTimestamp * 1000);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  toggleStyle() {
    this.isCustomStyleActive = !this.isCustomStyleActive;
    if (this.isCustomStyleActive === true) {
      this.buttonMaxWidthTitle = this.translateService.instant(
        'costcenter.costcenter_usage.reset'
      );
    } else {
      this.buttonMaxWidthTitle = this.translateService.instant(
        'costcenter.costcenter_usage.max_width'
      );
    }
  }

  selectView(type: GanttViewType) {
    this.selectedViewType = type;
    this.options.viewType = type;
  }

  scrollToToday() {
    this.ganttComponent.scrollToToday();
  }

  onCheckboxClick(e: MatCheckboxChange, item: FlatNode) {
    const selectedObj = this.findObjectByName(TREE_DATA, item.id);
    item.checked = e.checked;

    if (selectedObj?.childrenCount > 0) {
      selectedObj.children.forEach(child => {
        if (e.checked === true) {
          this.selectedItems.push(child.id);
          child.checked = true;

          this.cd.detectChanges();
        } else if (e.checked === false) {
          this.selectedItems = this.selectedItems.filter(
            selectedItem => selectedItem !== child.id
          );
          child.checked = false;
          this.cd.detectChanges();
        }
      });
    } else {
      if (e.checked === true) {
        this.selectedItems.push(selectedObj.id);
      } else if (e.checked === false) {
        this.selectedItems = this.selectedItems.filter(
          selectedItem => selectedItem !== item.id
        );
      }
    }

    this.isLoading = true;
    this.getGanttData(this.selectedItems);
  }

  GetCheckedState(node) {
    const checkbox = this.selectedItems.find(s => s === node.id);
    return checkbox ?? node.checked;
  }

  getQueryParam(param: string): string | null {
    const params = new URLSearchParams(window.location.search);

    return params.get(param);
  }
}
